import type { ComponentType, ReactNode } from 'react';
import { Card, CardContent } from './card';
import { cn } from '@eon/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export interface StatCardProps {
  label: string;
  value: ReactNode;
  /** Secondary line below the value (no trend icon). */
  subtext?: ReactNode;
  hint?: ReactNode;
  trend?: 'up' | 'down' | 'flat';
  icon?: ComponentType<{ className?: string }>;
  className?: string;
}

const trendConfig = {
  up: {
    color: 'text-energy-500',
    bg: 'bg-energy-500/10',
    Icon: TrendingUp,
  },
  down: {
    color: 'text-destructive',
    bg: 'bg-destructive/10',
    Icon: TrendingDown,
  },
  flat: {
    color: 'text-muted-foreground',
    bg: 'bg-muted/50',
    Icon: Minus,
  },
} satisfies Record<
  NonNullable<StatCardProps['trend']>,
  { color: string; bg: string; Icon: ComponentType<{ className?: string }> }
>;

export function StatCard({
  label,
  value,
  subtext,
  hint,
  trend = 'flat',
  icon: Icon,
  className,
}: StatCardProps) {
  const trendCfg = trendConfig[trend];
  const TrendIcon = trendCfg.Icon;

  return (
    <Card
      className={cn(
        'group relative overflow-hidden bg-gradient-to-br from-card to-card/60',
        'transition-all duration-300 hover:-translate-y-0.5 hover:shadow-glow',
        className,
      )}
    >
      {/* Top-right gradient orb */}
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-primary/8 blur-2xl transition-all duration-500 group-hover:bg-primary/15" />

      <CardContent className="flex items-start justify-between gap-4 p-5">
        <div className="flex min-w-0 flex-col gap-1.5">
          <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
            {label}
          </p>
          <p className="font-display text-2xl font-bold leading-none tracking-tight sm:text-3xl">{value}</p>
          {subtext ? (
            <p className="text-muted-foreground text-xs leading-snug">{subtext}</p>
          ) : null}
          {hint ? (
            <div className={cn('flex items-center gap-1 text-xs font-medium', trendCfg.color)}>
              <TrendIcon className="h-3 w-3" />
              <span>{hint}</span>
            </div>
          ) : null}
        </div>
        {Icon ? (
          <div
            className={cn(
              'bg-primary/10 text-primary ring-primary/20 shrink-0 rounded-xl p-3 ring-1',
              'transition-all duration-300 group-hover:bg-primary/15 group-hover:ring-primary/30',
            )}
          >
            <Icon className="h-6 w-6" />
          </div>
        ) : null}
      </CardContent>

      {/* Bottom glow accent */}
      <div className="from-primary/20 pointer-events-none absolute inset-x-0 -bottom-12 h-24 bg-gradient-to-t to-transparent blur-2xl" />
    </Card>
  );
}
